import { useCallback, useMemo } from 'react';
import { ApolloProvider } from '@apollo/react-common';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Formik } from 'formik';
import { ApolloClient } from 'apollo-client';
import posthog from 'posthog-js';
import FormTableOfContentsProvider from 'components/Form/TableOfContents/Provider';
import Form from 'components/Form/Form';
import Field from 'components/Form/Field/Field';
import StationSelector from 'components/Placement/Form/StationSelector/StationSelector';
import FormDatePicker from 'components/Form/DatePicker/DatePicker';
import FormTextArea from 'components/Form/TextArea/TextArea';
import UIGrid from 'components/UI/Grid/Grid';
import UIFormActions from 'components/UI/Form/Actions/Actions';
import AuditLogIcon from 'components/AuditLog/Icon/AuditLogIcon';
import UIButton from 'components/UI/Button/Button';
import useDialog from 'components/UI/Dialog/useDialog';
import UIDialog from 'components/UI/Dialog/Dialog';
import './DialogForm.scss';
import { watchApiClient } from '../../../../watchApi/watchApi';
import type { Station } from '../../../../watchApi/types';
import GET_MANUAL_VIEWERSHIP_BY_ID from '../../ViewershipTable/graphql/getManualViewershipById';
import { UPDATE_MANUAL_VIEWERSHIP } from '../../ViewershipTable/graphql/updateManualViewershipById';
import { CREATE_MANUAL_VIEWERSHIP } from '../../ViewershipTable/graphql/createManualViewership';
import { DELETE_MANUAL_VIEWERSHIP } from '../../ViewershipTable/graphql/deleteManualViewership';
import { validationSchema } from './validationSchema';

type ManualViewershipFormProps = {
  readonly manualViewershipId?: number;
  readonly placementId: number | undefined;
  readonly benSourceId: number;
  readonly watchSourceId: number;
  readonly onClose: () => void;
  readonly benApolloClient: ApolloClient<object>;
};

type FormValues = {
  premiereImpressions: number | null;
  past30Impressions: number | null;
  past60Impressions: number | null;
  past90Impressions: number | null;
  past365Impressions: number | null;
  station: Station;
  premiereDate: string;
  notes: string;
};

const getBody = ({
  values,
  benSourceId,
  id,
  watchSourceId,
}: {
  values: FormValues;
  benSourceId: number;
  id?: number;
  watchSourceId?: number;
}) => {
  const formattedDate = new Date(values.premiereDate).toISOString();
  return {
    benSourceId,
    ...(id && { id }),
    ...(watchSourceId && { watchSourceId }),
    watchStationId: Number(values.station.id),
    premiereDate: formattedDate,
    premiereImpressions: Number(values.premiereImpressions),
    past30Impressions: Number(values.past30Impressions),
    past60Impressions: Number(values.past60Impressions),
    past90Impressions: Number(values.past90Impressions),
    past365Impressions: Number(values.past365Impressions),
    ...(values.notes && { notes: values.notes }),
  };
};

function getImpressionValue(value: number | null | undefined): number | null {
  return value && value > 0 ? value : null;
}

// eslint-disable-next-line   max-lines-per-function
function ManualViewershipDialogForm({
  manualViewershipId,
  placementId,
  benSourceId,
  watchSourceId,
  onClose,
  benApolloClient,
}: ManualViewershipFormProps) {
  const deleteDialog = useDialog();

  const [createManualViewership] = useMutation(CREATE_MANUAL_VIEWERSHIP, {
    client: benApolloClient,
  });
  const [updateManualViewership] = useMutation(UPDATE_MANUAL_VIEWERSHIP, {
    client: benApolloClient,
  });
  const [deleteManualViewership, { loading: isDeleting }] = useMutation(
    DELETE_MANUAL_VIEWERSHIP,
    {
      client: benApolloClient,
      onCompleted: () => {
        posthog?.capture('pp:manual_viewership_deleted', {
          manualViewershipId: manualViewershipId?.toString(),
          placementId: Number(placementId),
          sourceId: Number(benSourceId),
          watchSourceId: Number(watchSourceId),
        });
      },
    },
  );

  const handleDelete = async () => {
    if (!manualViewershipId) return;

    await deleteManualViewership({
      variables: { id: manualViewershipId },
    });
    deleteDialog.close();
    onClose();
  };

  const { data: viewershipData, loading } = useQuery(
    GET_MANUAL_VIEWERSHIP_BY_ID,
    {
      variables: { id: Number(manualViewershipId) },
      skip: !manualViewershipId, // Skip query if no id is provided
      client: benApolloClient,
    },
  );

  const initialValues: FormValues = useMemo((): FormValues => {
    if (viewershipData?.manualViewership) {
      const viewership = viewershipData.manualViewership;
      return {
        premiereImpressions: getImpressionValue(viewership.premiereImpressions),
        past30Impressions: getImpressionValue(viewership.past30Impressions),
        past60Impressions: getImpressionValue(viewership.past60Impressions),
        past90Impressions: getImpressionValue(viewership.past90Impressions),
        past365Impressions: getImpressionValue(viewership.past365Impressions),
        station: viewership.watchStation ?? {
          id: 0,
          displayName: '',
          country: '',
          image: '',
        },
        premiereDate: viewership.premiereDate ?? '',
        notes: viewership.notes ?? null,
      };
    }

    return {
      premiereImpressions: null,
      past30Impressions: null,
      past60Impressions: null,
      past90Impressions: null,
      past365Impressions: null,
      station: {
        id: 0,
        displayName: '',
        country: '',
        image: '',
      },
      premiereDate: '',
      notes: '',
    };
  }, [viewershipData]);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleSubmit = useCallback(
    async (
      values: FormValues,
      actions: { setSubmitting: (isSubmitting: boolean) => void },
    ) => {
      const eventType = manualViewershipId ? 'updated' : 'created';
      let id = manualViewershipId;

      if (eventType === 'updated') {
        await updateManualViewership({
          variables: getBody({ values, benSourceId, id, watchSourceId }),
        });
      } else {
        const response = await createManualViewership({
          variables: getBody({ values, benSourceId, watchSourceId }),
        });

        id = response.data?.createManualViewership?.id;
      }

      posthog.capture(`pp:manual_viewership_${eventType}`, {
        manualViewershipId: id,
        placementId: Number(placementId),
        sourceId: benSourceId?.toString(),
        watchSourceId: watchSourceId?.toString(),
        stationId: values.station.id?.toString(),
        premiereDate: values.premiereDate?.toString(),
      });

      actions.setSubmitting(false);
      onClose();
    },
    [
      manualViewershipId,
      placementId,
      watchSourceId,
      updateManualViewership,
      benSourceId,
      createManualViewership,
      onClose,
    ],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <UIDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        title="Delete Manual Viewership"
        description="You are about to delete this manual viewership. Do you wish to proceed?"
        renderActions={() => (
          <>
            <UIButton theme="text" onClick={deleteDialog.close}>
              Cancel
            </UIButton>
            <UIButton
              theme="contained-red"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Confirm
            </UIButton>
          </>
        )}
      />

      <Formik
        validateOnMount
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const { isSubmitting } = formik;
          return (
            <Form
              formik={formik}
              className="manual-viewership-dialog-form__wrapper"
            >
              <FormTableOfContentsProvider formik={formik}>
                <section>
                  <h3>Basic Info</h3>
                  <UIGrid.Row>
                    <UIGrid.Group size="large">
                      <ApolloProvider client={watchApiClient}>
                        <Field
                          component={StationSelector}
                          name="station"
                          label="Station"
                          required
                        />
                      </ApolloProvider>
                    </UIGrid.Group>
                    <UIGrid.Group size="large">
                      <Field
                        component={FormDatePicker}
                        name="premiereDate"
                        label="Premiere Date"
                        required
                      />
                    </UIGrid.Group>
                  </UIGrid.Row>
                </section>

                <section>
                  <h3>Impressions</h3>
                  <UIGrid.Row>
                    <UIGrid.Group size="medium">
                      <Field
                        type="number"
                        name="premiereImpressions"
                        label="Premiere"
                      />
                    </UIGrid.Group>
                    <UIGrid.Group size="medium">
                      <Field
                        type="number"
                        name="past30Impressions"
                        label="30 Days"
                      />
                    </UIGrid.Group>
                    <UIGrid.Group size="medium">
                      <Field
                        type="number"
                        name="past60Impressions"
                        label="60 Days"
                      />
                    </UIGrid.Group>
                    <UIGrid.Group size="medium">
                      <Field
                        type="number"
                        name="past90Impressions"
                        label="90 Days"
                      />
                    </UIGrid.Group>
                    <UIGrid.Group size="medium">
                      <Field
                        type="number"
                        name="past365Impressions"
                        label="365 Days"
                      />
                    </UIGrid.Group>
                  </UIGrid.Row>
                </section>

                <section>
                  <h3>Additional Information</h3>
                  <UIGrid.Row>
                    <UIGrid.Group size="xx-large">
                      <Field
                        component={FormTextArea}
                        name="notes"
                        id="notes"
                        rows={4}
                        aria-label="Notes"
                        placeholder="Add any additional notes here"
                      />
                    </UIGrid.Group>
                  </UIGrid.Row>
                </section>
                <section>
                  <UIFormActions>
                    <UIButton
                      theme="contained-green"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      Save
                    </UIButton>
                    {manualViewershipId && (
                      <>
                        <UIButton
                          theme="contained-red"
                          onClick={() => {
                            posthog?.capture(
                              'pp:manual_viewership_delete_click',
                              {
                                manualViewershipId: manualViewershipId.toString(),
                                placementId: Number(placementId),
                                sourceId: Number(benSourceId),
                                watchSourceId: Number(watchSourceId),
                              },
                            );

                            deleteDialog.open();
                          }}
                        >
                          Delete
                        </UIButton>
                        <AuditLogIcon
                          entityType="manual-viewership"
                          entityId={manualViewershipId}
                          position="left-start"
                        />
                      </>
                    )}
                    <UIButton onClick={onClose}>Cancel</UIButton>
                  </UIFormActions>
                </section>
              </FormTableOfContentsProvider>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export default ManualViewershipDialogForm;
